<template>
  <v-container ma-0 pa-0>
    <v-file-input
      v-if="items && items.existing && items.existing.length < limitNumberFile"
      v-bind="$attrs"
      :placeholder="placeholder"
      prepend-icon="mdi-paperclip"
      :loading="loading"
      v-model="newUpload"
      :disabled="disabled || (items && items.existing && items.existing.length >= limitNumberFile)"
      :accept="accept"
      :rules="rulesArray"
      @change="onUpload($event)"
      :error-messages="errors.message"
      @input="clearErrors"
    />
    <v-text-field
      v-bind="$attrs"
      v-for="item in items.existing"
      :key="item.id"
      prepend-icon="mdi-paperclip"
      :value="item.originalFilename + ' (' + readableBytes(item.size) + ')'"
      :clearable="allowRemoval"
      readonly
      :append-icon="copyIcon"
      @click:append="onCopy(item)"
      @click:clear="onDeleteItem(item)"
    />
    <GoogleReCaptcha v-if="isPublic" :showLegend="false" />
    <v-snackbar v-model="showLinkCopied" :timeout="2000" color="success" right top>
      {{ $i18n.translate("Link copied") }}
    </v-snackbar>
  </v-container>
</template>

<script>
import GoogleReCaptcha from "@/gapp-components/components/display/GoogleReCaptcha.vue";
import ApiService from "@/gapp-components/services/api.service";
import { mapGetters } from "vuex";

export default {
  components: { GoogleReCaptcha },
  name: "UploadField",
  props: {
    value: [Array, Object],
    disabled: Boolean,
    limitNumberFile: {
      type: Number,
      required: false,
      default: 12
    },
    rules: Array,
    allowRemoval: {
      default: true,
      type: Boolean
    },
    uploadOnBehalfOfParticipantId: Number,
    allowedfileExtensions: { type: Array, required: false },
    public: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      default: ""
    },
    allowCopyUrl: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    loading: false,
    rulesArray: [],
    uploadMessage: "",
    newUpload: null,
    items: {
      existing: [],
      deleted: []
    },
    errors: {},
    showLinkCopied: false
  }),
  methods: {
    clearErrors() {
      this.errors = {};
    },
    onInput() {
      this.$emit("input", this.items);
      this.$emit("change");
    },
    onDeleteItem(item) {
      this.$emit("delete", item);
      const index = this.items.existing.indexOf(item);
      let deletedItem = this.items.existing.splice(index, 1);
      if (deletedItem && deletedItem.length > 0 && deletedItem[0].id) {
        this.items.deleted = this.items.deleted.concat(deletedItem);
      }
      this.$emit("input", this.items);
      this.$emit("change");
    },
    onUpload(file) {
      this.clearErrors();

      let uploadFile;

      if (Array.isArray(file)) {
        uploadFile = file[0];
      } else {
        uploadFile = file;
      }

      if (!(uploadFile instanceof File)) {
        console.warn("Invalid upload payload", uploadFile);
        return;
      }

      if (uploadFile.size === 0) {
        console.warn("Empty file upload");
        return;
      }

      let allowedExtensions =
        this.allowedfileExtensions && this.allowedfileExtensions.length > 0
          ? new RegExp("(" + this.allowedfileExtensions.map(x => "." + x).join("|") + ")$", "i")
          : /(.jpg|.jpeg|.png|.pdf|.doc|.docx|.ppt|.xls|.xlsx|.heic)$/i;

      if (!allowedExtensions.exec(uploadFile.name)) {
        this.$emit("uploadError", "Invalid file type");
        return;
      }

      let formData = new FormData();
      formData.append("file", uploadFile);

      if (this.uploadOnBehalfOfParticipantId) {
        formData.append("participantId", this.uploadOnBehalfOfParticipantId);
      }

      let endpointUrl = "/api/uploads";
      if (this.isPublic) {
        if (!this.selectedProgram || !this.selectedProgram.programKey) {
          this.$emit("uploadError", this.$i18n.translate("Unable to determine program for upload."));
          return;
        }
        endpointUrl = "/api/uploads/byProgramKey/" + this.selectedProgram.programKey + "/public";
      }

      this.loading = true;

      ApiService.postWithCaptcha(endpointUrl, formData, null, this.isPublic ? "publicUpload" : undefined)
        .then(({ data }) => {
          this.items.existing.push(data);
          this.$emit("input", this.items);
          this.$emit("change");
          this.$emit("newUpload", data);
          this.newUpload = null;
        })
        .catch(error => {
          this.errors = ApiService.getErrorsFromResponse(error);
          this.newUpload = null;
          this.$emit("uploadError", this.errors.message || this.$i18n.translate("Error uploading file."));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    readableBytes(bytes) {
      var i = Math.floor(Math.log(bytes) / Math.log(1024)),
        sizes = ["B", "kB", "MB", "GB"];
      return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + " " + sizes[i];
    },
    fileValidation(file) {
      this.errors = this.errors || {};
      let allowedExtensions =
        this.allowedfileExtensions && this.allowedfileExtensions.length > 0
          ? new RegExp("(" + this.allowedfileExtensions.map(x => "." + x).join("|") + ")$", "i")
          : /(.jpg|.jpeg|.png|.pdf|.doc|.docx|.ppt|.xls|.xlsx|.heic)$/i;
      if (file && !allowedExtensions.exec(file.name)) {
        return this.$i18n.translate("Acceptable file types") + ": " + this.accept;
      } else {
        return true;
      }
    },
    resetRulesArray() {
      this.rulesArray = [];
      if (this.rules) {
        this.rulesArray = this.rulesArray.concat(this.rules);
      }
      this.rulesArray = this.rulesArray.concat(v => this.fileValidation(v));
    },
    onCopy(item) {
      if (!this.allowCopyUrl) return;
      navigator.clipboard.writeText(item.href);
      this.showLinkCopied = true;
    }
  },
  computed: {
    ...mapGetters(["selectedProgram"]),
    placeholder() {
      if (this.uploadMessage && this.uploadMessage.length > 0) {
        return this.uploadMessage;
      } else {
        return this.message + this.$i18n.translate("Limit") + " " + this.limitNumberFile + " (" + this.accept + ")";
      }
    },
    accept() {
      return this.allowedfileExtensions && this.allowedfileExtensions.length > 0
        ? this.allowedfileExtensions.map(x => "." + x).join(", ")
        : ".jpg, .jpeg, .png, .pdf, .doc, .docx, .ppt, .xls, .xlsx, .heic";
    },
    isPublic() {
      return this.public;
    },
    copyIcon() {
      return this.allowCopyUrl ? "mdi-link" : "";
    }
  },
  watch: {
    value: {
      handler(v) {
        if (v && v.existing) {
          this.items.existing = v.existing;
        } else {
          this.items.existing = [];
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    //this.items.existing = this.value.existing;
    this.resetRulesArray();
  }
};
</script>
